import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Link2, Loader2, CheckCircle, XCircle, Copy, RefreshCw, Users, KeyRound, Activity,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import ToastMsg from '../../../components/common/ToastMsg';
import { cn } from '../../../utils/tailwindUtils';

const SWITCHES = [
  { key: 'push_appointments',     label: 'Send booked enquiries to eMedHub',
    hint: "Moving an enquiry to Appointment Booked sends the patient's details to eMedHub as an appointment request; front office picks the slot when approving it." },
  { key: 'sync_patients',         label: 'Receive new eMedHub patients',
    hint: 'Every patient registered in eMedHub appears in the CRM; open enquiries for the same mobile are converted.' },
  { key: 'sync_referral_doctors', label: 'Sync referral doctors',
    hint: "eMedHub's referral doctor list is pulled every hour (and on Sync now)." },
];

function Card({ title, icon: Icon, children, action }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
          <Icon className="w-4 h-4 text-indigo-500" /> {title}
        </h2>
        {action}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function CopyField({ label, value }) {
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</div>
      <div className="flex gap-2">
        <input readOnly value={value || ''}
          className="flex-1 min-w-0 px-3 py-2 text-xs font-mono rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200" />
        <button type="button"
          onClick={() => { navigator.clipboard?.writeText(value || ''); ToastMsg('success', 'Copied'); }}
          className="px-3 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
          <Copy className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function EmedhubLinkSettings() {
  const navigate = useNavigate();
  const { fetch: apiFetch, update, create } = useApiMethods();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ base_url: '', service_key: '' });
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState('');

  const load = async () => {
    const resp = await apiFetch('engage/emedhub/config/');
    if (resp?.status === 'success') {
      setConfig(resp.data);
      setForm({ base_url: resp.data.base_url || '', service_key: '' });
    } else {
      ToastMsg('error', resp?.message || 'Could not load eMedHub settings');
    }
    const ev = await apiFetch('engage/emedhub/events/');
    if (ev?.status === 'success') setEvents(ev.results || []);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async (patch, label = 'save') => {
    setBusy(label);
    const resp = await update('engage/emedhub/config/', patch);
    setBusy('');
    if (resp?.status === 'success') {
      setConfig(resp.data);
      setForm(f => ({ ...f, service_key: '' }));
      ToastMsg('success', 'Saved');
    } else {
      ToastMsg('error', resp?.message || 'Save failed');
    }
  };

  const action = async (url, label) => {
    setBusy(label);
    const resp = await create(url, {});
    setBusy('');
    ToastMsg(resp?.status === 'success' ? 'success' : 'error', resp?.message || 'Done');
    if (resp?.data && resp.data.webhook_url !== undefined) setConfig(resp.data);
    else load();
  };

  if (!config) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  const connected = !!config.last_tested_at && !config.last_error;

  return (
    <div className="p-4 max-w-[960px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-sm">
          <Link2 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">eMedHub Link</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Connect this CRM to the hospital's eMedHub HIMS: bookings go out as appointment requests,
            patients and referral doctors come back in.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer">
          <input type="checkbox" checked={config.is_enabled} disabled={busy === 'toggle'}
            onChange={e => save({ is_enabled: e.target.checked }, 'toggle')}
            className="w-4 h-4 accent-indigo-600" />
          {config.is_enabled ? 'Link on' : 'Link off'}
        </label>
      </div>

      <Card title="1. Connection to eMedHub" icon={KeyRound}
        action={
          <span className={cn('inline-flex items-center gap-1 text-xs font-medium',
            connected ? 'text-green-600' : config.last_error ? 'text-red-600' : 'text-gray-400')}>
            {connected ? <CheckCircle className="w-3.5 h-3.5" /> : config.last_error ? <XCircle className="w-3.5 h-3.5" /> : null}
            {connected ? `Connected — ${config.emedhub_clinic_name || 'eMedHub'}` : config.last_error ? 'Error' : 'Not tested'}
          </span>
        }>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">eMedHub API URL</div>
            <input value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })}
              placeholder="https://api.emedhub.in"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 dark:text-white" />
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Service key {config.service_key_preview && <span className="font-mono">({config.service_key_preview})</span>}
            </div>
            <input type="password" value={form.service_key}
              onChange={e => setForm({ ...form, service_key: e.target.value })}
              placeholder={config.service_key_preview ? 'Leave blank to keep the saved key' : 'Issued in eMedHub for this clinic'}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 dark:text-white" />
          </div>
        </div>
        {config.last_error && (
          <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{config.last_error}</div>
        )}
        <div className="flex gap-2">
          <button onClick={() => save(form)} disabled={!!busy}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {busy === 'save' ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => action('engage/emedhub/config/test/', 'test')} disabled={!!busy}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            {busy === 'test' ? 'Testing…' : 'Test connection'}
          </button>
        </div>
      </Card>

      <Card title="2. Paste into eMedHub (CRM connection)" icon={Link2}
        action={
          <button onClick={() => action('engage/emedhub/config/rotate-secret/', 'rotate')} disabled={!!busy}
            className="text-xs text-gray-500 hover:text-red-600">Rotate secret</button>
        }>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          eMedHub signs every patient and appointment update it sends with this secret.
          Rotating it stops updates until the new one is pasted into eMedHub.
        </p>
        <CopyField label="Webhook URL" value={config.webhook_url} />
        <CopyField label="Signing secret" value={config.hmac_secret} />
      </Card>

      <Card title="3. What to sync" icon={RefreshCw}
        action={
          <button onClick={() => action('engage/emedhub/config/sync-doctors/', 'sync')}
            disabled={!!busy || !config.is_enabled}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-40">
            <Users className="w-3.5 h-3.5" /> {busy === 'sync' ? 'Syncing…' : 'Sync referral doctors now'}
          </button>
        }>
        {SWITCHES.map(s => (
          <label key={s.key} className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={!!config[s.key]} disabled={!!busy}
              onChange={e => save({ [s.key]: e.target.checked }, s.key)}
              className="mt-0.5 w-4 h-4 accent-indigo-600" />
            <span>
              <span className="block text-sm font-medium text-gray-800 dark:text-gray-100">{s.label}</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">{s.hint}</span>
            </span>
          </label>
        ))}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 text-xs text-gray-600 dark:text-gray-300">
          <div>Bookings sent: <b>{config.counts?.appointments_sent ?? 0}</b></div>
          <div>Bookings failed: <b className={config.counts?.appointments_failed ? 'text-red-600' : ''}>{config.counts?.appointments_failed ?? 0}</b></div>
          <div>Doctors synced: <b>{config.last_doctor_sync_at || 'never'}</b></div>
          <div>Last event: <b>{config.last_event_at || 'none yet'}</b></div>
        </div>
      </Card>

      <Card title="Recent events from eMedHub" icon={Activity}>
        {events.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">Nothing received yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-1.5 pr-3 font-medium">Received</th>
                  <th className="py-1.5 pr-3 font-medium">Event</th>
                  <th className="py-1.5 pr-3 font-medium">Result</th>
                  <th className="py-1.5 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map(e => (
                  <tr key={e.id} className="border-t border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-200">
                    <td className="py-1.5 pr-3 whitespace-nowrap">{e.received_at}</td>
                    <td className="py-1.5 pr-3 font-mono">{e.event}</td>
                    <td className={cn('py-1.5 pr-3 font-medium',
                      e.result === 'error' ? 'text-red-600' : e.result === 'ignored' ? 'text-gray-400' : 'text-green-600')}>
                      {e.result}
                    </td>
                    <td className="py-1.5">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
